import React, { useMemo, useState } from "react"
import { Input } from "reactstrap"
import { groupBy, InputLabel, useSelector } from "swiipe.portal.shared"
import { translationSelectors } from "../../../store/reducers/translationSlice"
import useReduxDispatch from "../../../store/useReduxDispatch"
import { ITranslationItem } from "../../../type/translations/ITranslationItem"
import { ITranslationLanguageConfig } from "../../../type/translations/ITranslationLanguageConfig"
import SpinnerContainer from "../../buttons/SpinnerContainer"
import TranslationSection from "./TranslationSection"
import "./TranslationsForm.scss"
import TranslationTagFilterInput from "./TranslationTagFilterInput"

interface ITranslationsFormProps {
    languageConfigs: ITranslationLanguageConfig[]
    items: ITranslationItem[]
}

export interface ITermsTemplateTranslationRow {
    translation: string
    noFallback: boolean
}

export interface ITranslationsFormFile {
    fileUrl: string
    isInternal: boolean
}

const TranslationsForm = ({ languageConfigs, items }: ITranslationsFormProps) => {
    const dispatch = useReduxDispatch()
    const [isSaving, setIsSaving] = useState(false)
    const [disableSave, setDisableSave] = useState(false)
    const [filterText, setFilterText] = useState("")
    const [filterTagNames, setFilterTagNames] = useState<string[]>([])
    const itemsCalculatedTagNames = useSelector(translationSelectors.itemsCalculatedTagNames)

    const lowercaseFiltertext = filterText.toLowerCase()
    const shouldFilterByText = filterText.length >= 3
    const filteredItemsByText = !shouldFilterByText
        ? items
        : items.filter(
              (i) =>
                  i.lowercaseKey.includes(lowercaseFiltertext) ||
                  i.lowercaseTranslations.find((lt) => lt.content.includes(lowercaseFiltertext))
          )

    const shouldFilterByTags = filterTagNames.length > 0
    const filteredItemsByTags = !shouldFilterByTags
        ? items
        : items.filter((i) =>
              itemsCalculatedTagNames[i.group]?.[i.key]?.find((itemTagsName) => filterTagNames.includes(itemTagsName))
          )

    const filteredItems = !shouldFilterByTags
        ? filteredItemsByText
        : !shouldFilterByText
        ? filteredItemsByTags
        : filteredItemsByText.filter((i) => filteredItemsByTags.some((tagI) => i.id === tagI.id))
    const groups = useMemo(() => groupBy(filteredItems, (i) => i.group), [filteredItems])

    return (
        <div className="translations-form">
            <SpinnerContainer showSpinner={isSaving} aboveContent fullScreen>
                <div className="translations-form__filter-container">
                    <InputLabel title="Filter translations (min. 3 chars)" className="mt-3">
                        <Input
                            className="translations-filter-input"
                            onChange={(e) => {
                                setFilterText(e.currentTarget.value)
                            }}
                            value={filterText}
                        />
                    </InputLabel>
                    <InputLabel title="Filter by tags" className="mt-3">
                        <TranslationTagFilterInput
                            className="translations-filter-input-tags"
                            tagNames={filterTagNames}
                            onAdd={(tagName) => {
                                setFilterTagNames([...filterTagNames.filter((tn) => tn !== tagName), tagName])
                            }}
                            onRemove={(tagName) => {
                                setFilterTagNames([...filterTagNames.filter((tn) => tn !== tagName)])
                            }}
                        />
                    </InputLabel>
                </div>

                <div className="translations-form__translations-container">
                    <div className="translations-form__translations-actions-container"></div>
                    {Object.keys(groups).map((groupName) => (
                        <TranslationSection
                            key={groupName}
                            groupItems={groups[groupName]}
                            subLabel={groups[groupName][0]?.group}
                            label={groups[groupName][0]?.label}
                        />
                    ))}
                </div>
            </SpinnerContainer>
        </div>
    )
}

export default TranslationsForm
